import Cookies from 'js-cookie'
import { CURRENT_LANG } from '@/helpers/lang'
import dayjs from 'dayjs'
import type { OpUnitType, Dayjs } from 'dayjs'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isBetween from 'dayjs/plugin/isBetween'
import duration from 'dayjs/plugin/duration'
import { LANGS } from '@/constants/lang'

export type LangsType = (typeof LANGS)[number]

export interface AdsDatePickerLocaleAndFormat {
  format: DATE_FORMAT_DATE_PICKER
  locale: string
}

export enum DATE_FORMAT_DATE_PICKER {
  YYYY_MM_DD = 'yyyy/MM/dd',
  DD_MM_YYYY = 'dd/MM/yyyy',
  MM_DD_YYYY = 'MM/dd/yyyy'
}

dayjs.extend(customParseFormat)
dayjs.extend(isSameOrBefore)
dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)
dayjs.extend(LocalizedFormat)
dayjs.extend(duration)

const DAYJS_LOCALE_LIST: Record<typeof CURRENT_LANG, any> = {
  fr: () => import('dayjs/locale/fr'),
  de: () => import('dayjs/locale/de'),
  en: () => import('dayjs/locale/en'),
  es: () => import('dayjs/locale/es'),
  id: () => import('dayjs/locale/id'),
  it: () => import('dayjs/locale/it'),
  nl: () => import('dayjs/locale/nl'),
  pl: () => import('dayjs/locale/pl'),
  'pt-br': () => import('dayjs/locale/pt'),
  ru: () => import('dayjs/locale/ru'),
  tr: () => import('dayjs/locale/tr'),
  zh: () => import('dayjs/locale/zh'),
  vi: () => import('dayjs/locale/vi'),
  th: () => import('dayjs/locale/th'),
  ko: () => import('dayjs/locale/ko'),
  ja: () => import('dayjs/locale/ja')
}
// Function to dynamically import locale
const loadLocale = async () => {
  ;(await DAYJS_LOCALE_LIST[CURRENT_LANG]()) || DAYJS_LOCALE_LIST['en']()
}

loadLocale()

export const LONG_ALL_FORMAT = 'dddd DD MMMM YYYY'
export const YYYY_MM_DD = 'YYYY-MM-DD'
export const SERVER_FORMAT = YYYY_MM_DD
export const API_V2_FORMAT = 'DD/MM/YYYY'
export const DATE_FORMAT: Record<DATE_FORMAT_DATE_PICKER, string> = {
  [DATE_FORMAT_DATE_PICKER.YYYY_MM_DD]: 'field.birthdate.format.YYYY/MM/DD',
  [DATE_FORMAT_DATE_PICKER.DD_MM_YYYY]: 'field.birthdate.format.DD/MM/YYYY',
  [DATE_FORMAT_DATE_PICKER.MM_DD_YYYY]: 'field.birthdate.format.MM/DD/YYYY'
}

export const convertDateFromServerFormat = (date?: string) =>
  dayjs(date, SERVER_FORMAT).format(API_V2_FORMAT)

export const convertDateToServerFormat = (date?: string) =>
  dayjs(date, API_V2_FORMAT).format(SERVER_FORMAT)

export const convertDateToMyFormat = (date?: Date, format?: string) => dayjs(date).format(format)

export const isValidDate = (date?: string) => dayjs(date).isValid()

export const toLocaleFormat = (
  date?: string,
  fromFormat = date?.includes('/') ? API_V2_FORMAT : SERVER_FORMAT
) => {
  if (!date) return ''
  return dayjs(date, fromFormat).format(getDateFormat())
}

export const getDateFormat = () => {
  const DATE_FORMATS = {
    ar: 'DD/MM/YYYY',
    de: 'DD/MM/YYYY',
    es: 'DD/MM/YYYY',
    fr: 'DD/MM/YYYY',
    en: 'DD/MM/YYYY',
    id: 'DD/MM/YYYY',
    it: 'DD/MM/YYYY',
    ja: 'YYYY/MM/DD',
    ko: 'YYYY/MM/DD',
    nl: 'DD/MM/YYYY',
    pl: 'DD/MM/YYYY',
    pt: 'DD/MM/YYYY',
    'pt-br': 'DD/MM/YYYY',
    ru: 'DD/MM/YYYY',
    th: 'DD/MM/YYYY',
    tr: 'DD/MM/YYYY',
    zh: 'YYYY/MM/DD',
    vi: 'DD/MM/YYYY'
  }

  if (Cookies.get('userLocalization') === 'us') {
    return 'MM/DD/YYYY'
  }

  return DATE_FORMATS[CURRENT_LANG as LangsType] || 'DD/MM/YYYY'
}

export const adsDatePickerLocaleAndFormat: Record<LangsType, AdsDatePickerLocaleAndFormat> = {
  ar: {
    format: DATE_FORMAT_DATE_PICKER.DD_MM_YYYY,
    locale: 'en-GB'
  },
  de: {
    format: DATE_FORMAT_DATE_PICKER.DD_MM_YYYY,
    locale: 'de'
  },
  es: {
    format: DATE_FORMAT_DATE_PICKER.DD_MM_YYYY,
    locale: 'es'
  },
  fr: {
    format: DATE_FORMAT_DATE_PICKER.DD_MM_YYYY,
    locale: 'fr'
  },
  en: {
    format: DATE_FORMAT_DATE_PICKER.DD_MM_YYYY,
    locale: 'en-GB'
  },
  id: {
    format: DATE_FORMAT_DATE_PICKER.DD_MM_YYYY,
    locale: 'id'
  },
  it: {
    format: DATE_FORMAT_DATE_PICKER.DD_MM_YYYY,
    locale: 'it'
  },
  ja: {
    format: DATE_FORMAT_DATE_PICKER.YYYY_MM_DD,
    locale: 'ja'
  },
  ko: {
    format: DATE_FORMAT_DATE_PICKER.YYYY_MM_DD,
    locale: 'ko'
  },
  nl: {
    format: DATE_FORMAT_DATE_PICKER.DD_MM_YYYY,
    locale: 'nl'
  },
  pl: {
    format: DATE_FORMAT_DATE_PICKER.DD_MM_YYYY,
    locale: 'pl'
  },
  pt: {
    format: DATE_FORMAT_DATE_PICKER.DD_MM_YYYY,
    locale: 'pt'
  },
  'pt-br': {
    format: DATE_FORMAT_DATE_PICKER.DD_MM_YYYY,
    locale: 'pt-br'
  },
  ru: {
    format: DATE_FORMAT_DATE_PICKER.DD_MM_YYYY,
    locale: 'ru'
  },
  th: {
    format: DATE_FORMAT_DATE_PICKER.DD_MM_YYYY,
    locale: 'en-GB'
  },
  tr: {
    format: DATE_FORMAT_DATE_PICKER.DD_MM_YYYY,
    locale: 'tr'
  },
  zh: {
    format: DATE_FORMAT_DATE_PICKER.YYYY_MM_DD,
    locale: 'zh'
  },
  vi: {
    format: DATE_FORMAT_DATE_PICKER.DD_MM_YYYY,
    locale: 'vi'
  }
}

export const now = () => dayjs()
export const getDateInstance = () => dayjs
export const isSameDateUnitFromNow = (date: Date, unit: OpUnitType) => dayjs().isSame(date, unit)
export const addMonthDate = (date: string, amount: number) => dayjs(date).add(amount, 'month')
export const isSameOrAfterDateNow = (targetDate: string) => dayjs(targetDate).isSameOrAfter(dayjs())
export const getYear = (date: Dayjs) => dayjs(date).year()
export const getDateWithAddedyear = (date?: string, numberOfYears = 1) =>
  dayjs(date).add(numberOfYears, 'year')
export const getNextYear = (date?: string) => getYear(getDateWithAddedyear(date))
export const isAfterDateNow = (targetDate?: string) => dayjs(targetDate).isAfter(now())
export const isAfterDayNow = (targetDate?: string) => dayjs(targetDate).endOf('day').isAfter(now())
export const isAfterDate = (targetDate1?: string, targetDate2?: string) =>
  dayjs(targetDate1).isAfter(targetDate2)
export const isBeforeDate = (targetDate1?: string, targetDate2?: string) =>
  dayjs(targetDate1).isBefore(targetDate2)
export const toLongDate = (date?: string) =>
  dayjs(date, YYYY_MM_DD).locale(CURRENT_LANG).format(LONG_ALL_FORMAT)
export const isSameYearNow = (year: number) => dayjs().set('year', year).isSame(dayjs(), 'year')
export const durationBetweenDates = (
  date1: Dayjs,
  date2: string,
  format: dayjs.QUnitType | OpUnitType = 'd'
) => dayjs(date1).diff(date2, format)
export const diffNow = (date: string) => dayjs().diff(dayjs(date), 'day')
export const isBetweenDate = (date: string, min: number, max: number) => {
  const dayBeforeArrival = dayjs().startOf('day').diff(dayjs(date), 'day')
  return dayBeforeArrival > min && dayBeforeArrival < max
}
export const isEighteenOrOlder = (date?: Date) =>
  dayjs(date).isSameOrBefore(dayjs().subtract(18, 'years'))

export const formatAdsDatePickerLocaleAndFormat = (): AdsDatePickerLocaleAndFormat => {
  if (Cookies.get('userLocalization') === 'us') {
    return { format: DATE_FORMAT_DATE_PICKER.MM_DD_YYYY, locale: 'en' } // special use case for us to avoid unexpected calendar behavior with format
  }

  return adsDatePickerLocaleAndFormat[CURRENT_LANG as LangsType]
}

export const isTodayAtLeastNDaysAfterToday = (date: string, numberOfDays: number) =>
  diffNow(date) >= numberOfDays

export const today = dayjs().format(SERVER_FORMAT)
